import { getPaymentStatusDisplay, getPaymentMethodTypeDisplay, Payment } from '../payment';
import { capitalizeFirstLetter, propExists, propExistsAndIsNonEmpty, timeStringFromISO8601 } from '../../reporting-core/utils';

export const COLUMN_NAME_PAYMENT_ID = 'Payment ID';
export const COLUMN_NAME_STATUS = 'Status';
export const COLUMN_NAME_AMOUNT = 'Amount (Tax-Inclusive)';
export const COLUMN_NAME_AMOUNT_PRE_TAX = 'Amount (Pre-tax)';
export const COLUMN_NAME_TAX_AMOUNT = 'Tax Amount';
export const COLUMN_NAME_AMOUNT_REFUNDED = 'Amount Refunded';
export const COLUMN_NAME_PAYMENT_METHOD = 'Payment Method';
export const COLUMN_NAME_CREDIT_CARD_BRAND = 'Credit Card Brand / Bank Name';
export const COLUMN_NAME_CREDIT_CARD_LAST4 = 'Card / Bank Last 4 Digits';
export const COLUMN_NAME_DATE = 'Date';
export const COLUMN_NAME_TIME = 'Time';
export const COLUMN_NAME_TIMESTAMP = 'Timestamp';
export const COLUMN_NAME_INVOICE_ID = 'Invoice #';
export const COLUMN_NAME_INVOICE_DESCRIPTION = 'Invoice Description';
export const COLUMN_NAME_CUSTOMER_ID = 'Customer ID';
export const COLUMN_NAME_CUSTOMER_FIRST_NAME = 'Customer First Name';
export const COLUMN_NAME_CUSTOMER_LAST_NAME = 'Customer Last Name';
export const COLUMN_NAME_CUSTOMER_BIRTHDAY = 'Customer Birthday';
export const COLUMN_NAME_CUSTOMER_EMAIL = 'Customer Email';
export const COLUMN_NAME_DISCOUNT_AMOUNT = 'Discount Amount';
export const COLUMN_NAME_DISCOUNT_CODE = 'Discount Code';
export const COLUMN_NAME_DISCOUNT_NAME = 'Discount Name';
export const COLUMN_NAME_REVENUE_ACCOUNTS = 'Accounting Revenue Accounts';
export const COLUMN_NAME_LOCATION_ID = 'Location ID';
export const COLUMN_NAME_LOCATION_NAME = 'Location Name';
export const COLUMN_NAME_STAFF_USER_ID = 'Facilitating Staff User ID';
export const COLUMN_NAME_STAFF_USER_FIRST_NAME = 'Facilitating Staff User First Name';
export const COLUMN_NAME_STAFF_USER_LAST_NAME = 'Facilitating Staff User Last Name';
export const COLUMN_NAME_STAFF_USER_EMAIL = 'Facilitating Staff User Email';
export const COLUMN_NAME_PROCESSOR_PAYMENT_ID = 'Processor Payment ID';

export const COLUMN_NAMES: string[] = [
  COLUMN_NAME_PAYMENT_ID,
  COLUMN_NAME_STATUS,
  COLUMN_NAME_AMOUNT,
  COLUMN_NAME_AMOUNT_PRE_TAX,
  COLUMN_NAME_TAX_AMOUNT,
  COLUMN_NAME_AMOUNT_REFUNDED,
  COLUMN_NAME_PAYMENT_METHOD ,
  COLUMN_NAME_CREDIT_CARD_BRAND,
  COLUMN_NAME_CREDIT_CARD_LAST4,
  COLUMN_NAME_DATE,
  COLUMN_NAME_TIME,
  COLUMN_NAME_TIMESTAMP,
  COLUMN_NAME_INVOICE_ID,
  COLUMN_NAME_INVOICE_DESCRIPTION,
  COLUMN_NAME_CUSTOMER_ID,
  COLUMN_NAME_CUSTOMER_FIRST_NAME,
  COLUMN_NAME_CUSTOMER_LAST_NAME,
  COLUMN_NAME_CUSTOMER_BIRTHDAY,
  COLUMN_NAME_CUSTOMER_EMAIL,
  COLUMN_NAME_DISCOUNT_AMOUNT,
  COLUMN_NAME_DISCOUNT_CODE,
  COLUMN_NAME_DISCOUNT_NAME,
  COLUMN_NAME_REVENUE_ACCOUNTS,
  COLUMN_NAME_LOCATION_ID,
  COLUMN_NAME_LOCATION_NAME,
  COLUMN_NAME_STAFF_USER_ID,
  COLUMN_NAME_STAFF_USER_FIRST_NAME,
  COLUMN_NAME_STAFF_USER_LAST_NAME,
  COLUMN_NAME_STAFF_USER_EMAIL,
  COLUMN_NAME_PROCESSOR_PAYMENT_ID,
];

export function getContentItem(payment: Payment,
                               selectedColumnName: string): string {
  switch (selectedColumnName) {
    case COLUMN_NAME_PAYMENT_ID:
      return payment.id.toString();
    case COLUMN_NAME_STATUS:
      return getPaymentStatusDisplay(payment.status);
    case COLUMN_NAME_AMOUNT:
      return payment.amount.toString();
    case COLUMN_NAME_AMOUNT_PRE_TAX:
      return payment.amount_pre_tax.toString();
    case COLUMN_NAME_TAX_AMOUNT:
      return payment.tax_amount.toString();
    case COLUMN_NAME_AMOUNT_REFUNDED:
      return payment.amount_refunded.toString();
    case COLUMN_NAME_PAYMENT_METHOD:
      return getPaymentMethodTypeDisplay(
        payment.payment_type,
        payment.payment_method_type
      );
    case COLUMN_NAME_CREDIT_CARD_BRAND:
      if (propExistsAndIsNonEmpty(payment.credit_card_brand)) {
        return capitalizeFirstLetter(payment.credit_card_brand);
      } else if (propExistsAndIsNonEmpty(payment.bank_account_bank_name)) {
        return capitalizeFirstLetter(payment.bank_account_bank_name);
      } else {
        return '';
      }
    case COLUMN_NAME_CREDIT_CARD_LAST4:
      if (propExistsAndIsNonEmpty(payment.credit_card_last4)) {
        return payment.credit_card_last4;
      } else if (propExistsAndIsNonEmpty(payment.bank_account_last4)) {
        return payment.bank_account_last4;
      } else {
        return '';
      }
    case COLUMN_NAME_DATE:
      return payment.created_date;
    case COLUMN_NAME_TIME:
      return timeStringFromISO8601(payment.created_at);
    case COLUMN_NAME_TIMESTAMP:
      return payment.created_at;
    case COLUMN_NAME_INVOICE_ID:
      return payment.invoice_id.toString();
    case COLUMN_NAME_INVOICE_DESCRIPTION:
      return payment.invoice_description;
    case COLUMN_NAME_CUSTOMER_ID:
      if (!propExists(payment.customer_id)) { return null; }
      return payment.customer_id.toString();
    case COLUMN_NAME_CUSTOMER_FIRST_NAME:
      return payment.customer_first_name;
    case COLUMN_NAME_CUSTOMER_LAST_NAME:
      return payment.customer_last_name;
    case COLUMN_NAME_CUSTOMER_BIRTHDAY:
      return payment.customer_birthday;
    case COLUMN_NAME_CUSTOMER_EMAIL:
      return payment.customer_email;
    case COLUMN_NAME_DISCOUNT_AMOUNT:
      return payment.discount_amount.toString();
    case COLUMN_NAME_DISCOUNT_CODE:
      return payment.discount_code;
    case COLUMN_NAME_DISCOUNT_NAME:
      return payment.discount_name;
    case COLUMN_NAME_REVENUE_ACCOUNTS:
      const colVals: string[] = [];
      for (const revenueAccountCode in payment.revenue_account_codes) {
        if (payment.revenue_account_codes.hasOwnProperty(revenueAccountCode)) {
          colVals.push(revenueAccountCode + ':' + payment.revenue_account_codes[revenueAccountCode].toFixed(3).toString());
        }
      }
      return colVals.join(',') + '\t';
    case COLUMN_NAME_LOCATION_ID:
      if (!propExists(payment.location_id)) { return null; }
      return payment.location_id.toString();
    case COLUMN_NAME_LOCATION_NAME:
      if (!propExists(payment.location_name)) { return null; }
      return payment.location_name;
    case COLUMN_NAME_STAFF_USER_ID:
      if (!propExists(payment.staff_user_id)) { return null; }
      return payment.staff_user_id.toString();
    case COLUMN_NAME_STAFF_USER_FIRST_NAME:
      return payment.staff_user_first_name;
    case COLUMN_NAME_STAFF_USER_LAST_NAME:
      return payment.staff_user_last_name;
    case COLUMN_NAME_STAFF_USER_EMAIL:
      return payment.staff_user_email;
    case COLUMN_NAME_PROCESSOR_PAYMENT_ID:
      if (payment.stripe_payment_intent_id !== '') {
        return payment.stripe_payment_intent_id;
      } else if (payment.gravity_external_transaction_id !== '') {
        return payment.gravity_external_transaction_id;
      } else {
        return '';
      }
    default:
      return 'COLUMN NOT IMPLEMENTED';
  }
}
